// src/pages/TermsOfServicePage.jsx
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Scale, Target, Shield, FileText, AlertTriangle, BookOpen, Link as LinkIcon, Mail, Globe, Lock } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Breadcrumbs from '../components/Shared/Breadcrumbs';

gsap.registerPlugin(ScrollTrigger);

const TermsOfServicePage = () => {
  const pageRef = useRef(null);

  const sections = [
    {
      id: 'website-purpose',
      title: 'Website Purpose',
      icon: Target,
      color: 'from-green-500 to-emerald-500',
      content: [
        'Information about our mealworm cultivation and protein manufacturing operations',
        'Business, partnership, and investment-related communication',
        'General educational and informational content',
        'This website does not constitute an offer to sell products, securities, or services unless expressly stated'
      ]
    },
    {
      id: 'eligibility-use',
      title: 'Eligibility & Acceptable Use',
      icon: Shield,
      color: 'from-blue-500 to-cyan-500',
      content: [
        'By using this website, you confirm that:',
        '- You are using the site for lawful business or informational purposes',
        '- You will not misuse, disrupt, or attempt unauthorized access to the website',
        '- You will not submit false, misleading, or harmful information',
        'Unauthorized use may result in termination of access and legal action'
      ]
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property Rights',
      icon: BookOpen,
      color: 'from-purple-500 to-pink-500',
      content: [
        'All content on this website, including but not limited to:',
        '- Text, graphics, logos, images',
        '- Process descriptions and diagrams',
        '- Brand names and trademarks',
        'are protected intellectual property',
        'You may not copy, reproduce, modify, distribute, or exploit any content without prior written consent'
      ]
    },
    {
      id: 'information-accuracy',
      title: 'Information Accuracy & Disclaimer',
      icon: AlertTriangle,
      color: 'from-yellow-500 to-amber-500',
      content: [
        'While we strive to ensure accuracy:',
        '- Content is provided for general informational purposes only',
        '- Technical, nutritional, or operational details may change without notice',
        '- No guarantees or warranties are made regarding completeness or accuracy',
        'Visitors should not rely solely on website content for business, investment, or regulatory decisions'
      ]
    },
    {
      id: 'no-advice',
      title: 'No Investment or Financial Advice',
      icon: Globe,
      color: 'from-red-500 to-orange-500',
      content: [
        'Nothing on this website constitutes:',
        '- Investment advice',
        '- Financial advice',
        '- An offer or solicitation of securities',
        'Any investment-related discussions will be conducted through formal channels and governed by separate agreements'
      ]
    },
    {
      id: 'third-party-links',
      title: 'Third-Party Links',
      icon: LinkIcon,
      color: 'from-indigo-500 to-blue-500',
      content: [
        'This website may contain links to third-party websites for reference or convenience',
        'We do not control and are not responsible for the content, accuracy, or policies of external websites',
        'Accessing third-party links is at your own risk'
      ]
    },
    {
      id: 'limitation-liability',
      title: 'Limitation of Liability',
      icon: Scale,
      color: 'from-gray-500 to-dark-600',
      content: [
        'To the fullest extent permitted by law:',
        '- We shall not be liable for any direct, indirect, incidental, or consequential damages',
        '- This includes damages arising from use of or inability to use the website',
        '- Or reliance on any information provided herein',
        'Use of this website is at your own discretion and risk'
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy',
      icon: Lock,
      color: 'from-teal-500 to-green-500',
      content: [
        'Your use of this website is also governed by our Privacy Policy',
        'The Privacy Policy explains how personal data is collected, used, and protected',
        'By using this website, you consent to the practices described in our Privacy Policy'
      ]
    },
    {
      id: 'modifications-terms',
      title: 'Modifications to Terms',
      icon: FileText,
      color: 'from-gold-500 to-yellow-500',
      content: [
        'We reserve the right to modify or update these Terms of Service at any time without prior notice',
        'Changes will be effective upon posting on this page',
        'Continued use of the website constitutes acceptance of revised terms',
        'We encourage regular review of these terms'
      ]
    },
    {
      id: 'governing-law',
      title: 'Governing Law & Jurisdiction',
      icon: Globe,
      color: 'from-blue-600 to-indigo-600',
      content: [
        'These Terms of Service shall be governed by and interpreted in accordance with the laws of India',
        'Any disputes arising in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in India',
        'International users agree to comply with all applicable local laws'
      ]
    }
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate section headers
      gsap.fromTo('.terms-section-header', {
        x: -50,
        opacity: 0,
      }, {
        x: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
        scrollTrigger: {
          trigger: '.terms-content',
          start: 'top 70%',
          toggleActions: 'play none none reverse',
        },
      });

      // Animate content cards with fade up
      gsap.fromTo('.terms-card', {
        y: 60,
        opacity: 0,
      }, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        scrollTrigger: {
          trigger: '.terms-card',
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      });

      // Scale animation for icons
      gsap.to('.scale-icon', {
        scale: 1.1,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
        stagger: 0.4,
      });

      // Hero section animation
      const tl = gsap.timeline();
      tl.from('.terms-hero-title', {
        y: 100,
        opacity: 0,
        duration: 1.2,
        ease: 'power3.out',
      })
      .from('.scale-icon-hero', {
        scale: 0,
        rotation: -180,
        duration: 1,
        ease: 'back.out(1.7)',
      }, '-=0.5');
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div ref={pageRef} className="pt-20">
      <Breadcrumbs currentPage="Terms of Service" />

      {/* Hero Section */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-green-900/20 to-dark-900" />
        <div className="absolute inset-0 glitter-bg" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="scale-icon-hero inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-green-600 to-emerald-600 mb-6">
              <Scale className="w-10 h-10 md:w-12 md:h-12 text-white" />
            </div>
            
            <h1 className="terms-hero-title text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">
                Terms of Service
              </span>
            </h1>
            
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-12 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500" />
              <p className="text-lg text-gray-300">
                Effective Date: {currentDate}
              </p>
              <div className="w-12 h-0.5 bg-gradient-to-r from-emerald-500 to-green-500" />
            </div>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
              Welcome to our website. By accessing or using this website, you agree to be bound by 
              the following Terms of Service. If you do not agree with these terms, please discontinue use.
            </p>
            
            <div className="inline-flex items-center px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
              <span className="text-green-400 text-sm">Governing Legal Agreement</span>
            </div>
          </div>
        </div>
      </section>


      {/* Main Content */}
      <section className="terms-content py-16 md:py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            {/* Introduction */}
            <div className="terms-card mb-12">
              <div className="bg-dark-900/50 rounded-2xl p-8 border border-green-500/20">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-lg text-gray-300 mb-4">
                      These Terms govern your use of our website and any related communications 
                      or inquiries made through it.
                    </p>
                    <p className="text-gray-400">
                      Please read these terms carefully before using our website. Your continued 
                      use constitutes acceptance of all terms and conditions.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-12">
              {sections.map((section, index) => (
                <div key={section.id} id={section.id} className="scroll-mt-24">
                  <div className="terms-section-header mb-8">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className={`scale-icon w-12 h-12 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center`}>
                        <section.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl md:text-3xl font-bold text-white">
                          {index + 1}. {section.title}
                        </h2>
                      </div>
                    </div>
                    <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full" />
                  </div>

                  <div className="terms-card">
                    <div className="bg-dark-800/30 rounded-xl border border-green-500/10 p-6 md:p-8">
                      <ul className="space-y-4">
                        {section.content.map((item, idx) => (
                          <li key={idx} className="flex items-start group">
                            <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 group-hover:scale-125 transition-transform" />
                            <span className="text-gray-300 group-hover:text-gray-200 transition-colors">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Section */}
            <div id="contact" className="mt-16 scroll-mt-24">
              <div className="terms-section-header mb-8">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold-500 to-yellow-500 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">
                    Contact Information
                  </h2>
                </div>
                <div className="w-24 h-1 bg-gradient-to-r from-gold-500 to-yellow-500 rounded-full" />
              </div>

              <div className="terms-card">
                <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-2xl border border-gold-500/20 p-8">
                  <p className="text-lg text-gray-300 mb-6">
                    For questions regarding these Terms of Service, please contact:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center space-x-4 p-4 bg-dark-900/50 rounded-xl border border-gold-500/20 hover:border-gold-500/40 transition-all duration-300">
                      <div className="w-10 h-10 rounded-full bg-gold-500/20 border border-gold-500/30 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-gold-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Email</p>
                        <a href="mailto:info@profitmix.in" className="text-gold-300 hover:text-gold-200 transition-colors">
                          info@profitmix.in
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 p-4 bg-dark-900/50 rounded-xl border border-gold-500/20 hover:border-gold-500/40 transition-all duration-300">
                      <div className="w-10 h-10 rounded-full bg-gold-500/20 border border-gold-500/30 flex items-center justify-center">
                        <span className="text-gold-400 text-lg">📍</span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Location</p>
                        <p className="text-gold-300">India</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-8 border-t border-gold-500/20">
                    <p className="text-center text-gray-400">
                      Legal inquiries will be addressed by our compliance team within 3-5 business days.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation & Acceptance */}
            <div className="mt-12 space-y-8">
              {/* Acceptance Box */}
              <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 rounded-xl border border-green-500/30 p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Acceptance of Terms</h3>
                    <p className="text-gray-400 text-sm">
                      By continuing to use this website, you acknowledge and agree to all terms
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-green-400 text-sm">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span>Active Agreement • Last Updated: {currentDate}</span>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <Link
                  to="/privacy"
                  className="px-6 py-3 border border-green-500/30 text-green-400 rounded-lg hover:bg-green-500/10 transition-all duration-300 flex items-center justify-center"
                >
                  ← View Privacy Policy
                </Link>
                
                <div className="flex gap-4">
                  <button
                    onClick={() => window.print()}
                    className="px-6 py-3 border border-green-500/30 text-green-400 rounded-lg hover:bg-green-500/10 transition-all duration-300"
                  >
                    Print Terms
                  </button>
                  
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-500 hover:to-emerald-500 transition-all duration-300"
                  >
                    Back to Top ↑
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;